(ns hai.tasks.card
  "Shared task row for the home dashboard's Tasks mini-app: the always-visible
   part (stamp, title, deadline, links) resting on a card that raises and presses
   to reveal a description, plus inline edit and delete-confirm. Required by both
   the desktop and mobile Tasks cards so the row is one implementation.

   Two surface differences are passed in: `:hover-actions?` hides edit/delete
   until hover (desktop pointer) versus keeping them shown (touch); `:show-hint?`
   toggles the keyboard submit hint on the edit form (off for touch)."
  (:require
   [clojure.string :as str]
   [garden.stylesheet]
   [hai.notes.data :as notes-data]
   [hai.notes.sections :as sections]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.ui :as ui]
   [hai.tasks.view :as view]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private trash-icon (icons/mui-icon :outlined :delete))
(def ^:private edit-icon (icons/mui-icon :outlined :edit))

(defn quick-add-component
  "Composer revealed by a card's Add-task button. Closes via `on-close` on
   cancel, escape, a successful save, or losing focus while the title is still
   empty. `show-hint?` toggles the keyboard submit hint (off for touch)."
  [{:keys [on-close show-hint?]}]
  (r/with-let [!wrap-el (atom nil)
               close! (fn []
                        (>e [:form/reset ::composer-form])
                        (on-close))
               on-save (fn [{:keys [title description urgency deadline urls assignee
                                    contacts notes tasks]}]
                         ;; Pass the full picked entity maps (not just uuids) so
                         ;; `::data/create` can embed them in the optimistic task
                         ;; as `:task/contacts` / `:task/notes` / `:task/tasks`
                         ;; and the new card renders link chips instantly. The
                         ;; event derives the mutation's `*-uuids` from these.
                         (>e [::data/create {:title title
                                             :description description
                                             :urgency urgency
                                             :deadline deadline
                                             :urls urls
                                             :assignee assignee
                                             :contacts contacts
                                             :notes notes
                                             :tasks tasks}])
                         (close!))]
    (let [empty-title? (str/blank? @(<s [:form.field/current-value ::composer-form :task/title]))
          me @(<s :auth/identity)]
      [ui/quick-add
       {:class (when-not empty-title? "open")
        :ref #(reset! !wrap-el %)
        :on-blur (fn [e]
                   (let [wrap @!wrap-el]
                     (when (and empty-title?
                                wrap
                                (not (.contains wrap (.-relatedTarget e))))
                       (close!))))}
       [ui/task-form {:form-id ::composer-form
                      :placeholder "Give your task a title…"
                      :primary-label "add"
                      :auto-focus? true
                      :collapsed-until-typed? true
                      :show-hint? show-hint?
                      :assignable? true
                      :initial-assignee me
                      :linkable? true
                      :on-save on-save
                      :on-cancel close!}]])))

;; The always-visible part of a task: checkbox, title, deadline, and any links.
;; A description is not here — it lives in [[task-detail]], revealed on press.
(o/defstyled task-row :div
  {:padding "14px 18px"
   :display "flex" :flex-direction "column" :gap "10px"
   :position "relative"}
  [:.task-row-main
   {:display "flex" :flex-direction "row"
    :gap "14px" :align-items "center"}]
  ;; Links indent to line up under the title, past the checkbox.
  [:.task-row-links
   {:margin-left "48px"}]
  [:.task-title-block
   {:flex "1" :min-width "0"
    :display "flex" :flex-direction "column" :gap "2px"}]
  [:.task-title
   {:font-size "15px" :font-weight "500" :line-height "1.35"
    :color t/--card-forest
    :word-break "break-word"
    :overflow-wrap "anywhere"}]
  [:.task-title.done {:opacity "0.5" :text-decoration "line-through"}]
  [:.stampbox
   {:width "34px" :height "34px"
    :border-width "2px" :border-style "dashed" :border-color t/--property-3
    :border-radius "8px"
    :background "rgba(255,255,255,0.5)"
    :position "relative"
    :flex "0 0 auto"
    :cursor "pointer" :outline "none"}
   [:&:focus-visible
    {:border-color t/--card-forest
     :box-shadow "0 0 0 2px #F7F6EF, 0 0 0 3.5px #2D3E2A"}]
   [:&.stamped
    {:border-color "rgba(45,62,42,0.18)"
     :background "rgba(255,255,255,0.3)"}]]
  ;; Hover feedback only where hover exists — on touch the browser pins :hover
  ;; to the last-tapped element, which would leave it stuck on.
  [:at-media {:hover "hover"}
   [:.stampbox [:&:hover {:border-style "solid" :border-color t/--card-forest}]]]
  [ui/completion-stamp
   {:position "absolute"
    :left "50%" :top "50%"
    :width "32px" :height "32px"}
   [:.check {:font-size "22px"}]]
  [:.actions
   {:display "flex" :align-items "center" :gap "2px"}]
  ;; Desktop reveals edit/delete only on hover; touch surfaces keep them shown.
  [:&.hover-actions
   [:.actions {:opacity "0"}]
   [:&:hover [:.actions {:opacity "1"}]]]
  [:.icon-btn
   {:width "26px" :height "26px" :padding "0"
    :border "0" :background "transparent"
    :border-radius "6px"
    :color t/--link-muted
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"}]
  [:.edit-btn
   [:&:hover {:background "rgba(45,62,42,0.10)" :color t/--card-forest}]]
  [:.delete-btn
   [:&:hover {:background "rgba(177,58,48,0.10)" :color t/--property-13}]])

;; A task's description, flush beneath the row inside the pressed card, rising in
;; as one continuous sheet. Its left inset lines the text up under the title.
(o/defstyled task-detail :div
  {:padding "8px 18px 16px 48px"
   :border-top "1px solid" :border-top-color t/--card-forest-10
   :animation "task-detail-in 0.22s ease"}
  [:.task-desc
   {:font-size "13px" :line-height "1.55"
    :color t/--card-forest
    :white-space "pre-wrap"
    :opacity "0.8"}]
  [:&.done [:.task-desc {:opacity "0.55"}]])

;; The surface a task sits on. A task with a description rests as a raised,
;; pressable card and presses down when opened; one with nothing to reveal stays
;; flat. Elevation — not a badge or chevron — is the signal that a task expands.
(o/defstyled task-shell :div
  {:border-radius "12px"
   :transition "transform 0.2s ease, box-shadow 0.2s ease, background 0.16s ease"}
  [:&.raised
   {:background t/--task-card-surface
    :border-radius "16px"
    :box-shadow t/--task-card-shadow
    :cursor "pointer"}]
  ;; Pressed down: shallow shadow, and the detail extrudes inside the rounded box.
  [:&.raised.open
   {:box-shadow t/--task-card-shadow-pressed
    :overflow "hidden"}]
  ;; Hover cues only where hover exists — a tap's sticky :hover would leave the
  ;; card floating (or the flat row tinted) until the next tap elsewhere.
  [:at-media {:hover "hover"}
   ;; Only a collapsed card lifts; the lift is the "press me" cue.
   ["&.raised:not(.open):hover"
    {:transform "translateY(-7px)"
     :box-shadow t/--task-card-shadow-hover}]
   ;; Flat rows get only a whisper of hover feedback — no lift, no card.
   [:&.flat
    [:&:hover {:background t/--card-forest-06}]]]
  ;; A just-added task holds a sage wash then fades — long enough to catch the
  ;; eye. A relative-positioned overlay keeps it off the card's own background
  ;; and drop shadow, so it works the same on flat rows and raised cards.
  [:&.just-added
   {:position "relative"}
   [:&::after
    {:content "''"
     :position "absolute" :inset "0"
     :border-radius "inherit"
     :pointer-events "none"
     :animation "task-just-added 2s ease-out forwards"}]])

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules just-added-keyframes
  (garden.stylesheet/at-keyframes
   :task-just-added
   ["0%"   {:background "rgba(45,62,42,0.16)"}]
   ["55%"  {:background "rgba(45,62,42,0.16)"}]
   ["100%" {:background "rgba(45,62,42,0)"}]))

(o/defstyled task-group :div
  {:transition "background 0.15s ease"}
  [:&.confirming
   {:background "rgba(177,58,48,0.05)"}])

(o/defstyled delete-confirm :div
  {:display "flex" :align-items "center" :justify-content "flex-end"
   :gap "8px"
   :padding "10px 22px 12px"
   :font-size "13px" :color t/--card-forest
   :border-bottom-width "1px" :border-bottom-style "solid" :border-bottom-color t/--property-3}
  [:.prompt {:margin-right "auto" :color t/--link-muted}]
  [:.delete-yes
   {:padding "6px 14px"
    :border "none" :border-radius "8px"
    :background t/--property-13 :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--property-14}]]
  [:.delete-no
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "13px" :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn task-row-component
  "One task row for the mini-app cards. `hover-actions?` hides edit/delete until
   hover (desktop) versus keeping them shown (touch); `show-hint?` toggles the
   edit form's keyboard submit hint."
  [task {:keys [hover-actions? show-hint?]}]
  (r/with-let [!expanded? (r/atom false)
               !confirming? (r/atom false)
               !editing? (r/atom false)]
    (let [task-uuid (:task/uuid task)
          title (:task/title task)
          description (:task/description task)
          links (:task/activities task)
          contacts (:task/contacts task)
          linked-notes (:task/notes task)
          linked-tasks (:task/tasks task)
          done? (some? (:task/completed-at task))
          has-description? (boolean (seq description))
          has-links? (boolean (seq links))
          has-entity-links? (boolean (or (seq contacts)
                                         (seq linked-notes)
                                         (seq linked-tasks)))
          ;; A description is the only thing hidden until you press. Links and
          ;; the deadline ride on the resting row, so they don't make a card. A
          ;; done task flattens — it reads as filed away, not still pressable.
          expandable? (and has-description? (not done?))
          open? (and expandable? @!expanded?)
          confirming? @!confirming?
          editing? @!editing?
          highlighted? @(<s [::data/highlighted? task-uuid])
          link-urls (vec (keep :activity/url links))
          start-edit (fn [] (reset! !editing? true))
          cancel-edit (fn [] (reset! !editing? false))
          save-edit (fn [{:keys [title description urgency deadline urls assignee]}]
                      (>e [::data/update {:task-uuid task-uuid
                                          :title title
                                          :description description
                                          :urgency urgency
                                          :deadline deadline
                                          :urls urls
                                          :clear-deadline? (and (:task/deadline-at task)
                                                                (nil? (:date deadline)))}])
                      ;; Reassignment goes through its own `task/assign`
                      ;; mutation, and only when a fresh assignee was picked
                      ;; (a contact or a typed email).
                      (when (data/picked-assignee? assignee)
                        (>e [::data/assign task-uuid assignee]))
                      (reset! !editing? false))
          toggle-done (fn [e]
                        (.stopPropagation e)
                        (>e [(if done? ::data/uncomplete ::data/complete) task-uuid]))]
      [task-group {:class (when confirming? "confirming")}
       (if editing?
         [ui/quick-add {:class "editing"}
          [ui/task-form {:form-id [::edit-form task-uuid]
                         :initial-value (cond-> {:task/title title
                                                 :task/urgency (get task :task/urgency :task.urgency/later)}
                                          (seq description) (assoc :task/description description)
                                          (:task/deadline-at task) (assoc :task/deadline (deadline/from-task task))
                                          (seq link-urls) (assoc :activity/urls link-urls))
                         :assignable? true
                         :initial-assignee (:task/assigned-to task)
                         :auto-focus? true
                         :placeholder "Title"
                         :primary-label "save"
                         :show-hint? show-hint?
                         :on-save save-edit
                         :on-cancel cancel-edit}]]
         [task-shell
          {:class (cond-> []
                    expandable? (conj "raised")
                    (not expandable?) (conj "flat")
                    open? (conj "open")
                    highlighted? (conj "just-added"))}
          [task-row
           {:class (cond-> []
                     done? (conj "done")
                     hover-actions? (conj "hover-actions"))
            :on-click (fn []
                        (when expandable?
                          (swap! !expanded? not)))}
           [:div.task-row-main
            [:div.stampbox
             {:class (when done? "stamped")
              :role "button"
              :tab-index 0
              :aria-label (if done? "remove stamp" "stamp complete")
              :on-click toggle-done
              :on-key-down (fn [e]
                             (when (or (= "Enter" (.-key e)) (= " " (.-key e)))
                               (.preventDefault e)
                               (toggle-done e)))}
             (when done?
               ^{:key (:task/completed-at task)}
               [ui/completion-stamp {:task-title title :centered? true}])]
            [:div.task-title-block
             [:div.task-title
              {:class (when done? "done")}
              title]
             (when-not done?
               [view/task-deadline {:task task}])]
            [:div.actions {:on-click #(.stopPropagation %)}
             [:button.icon-btn.edit-btn
              {:type "button"
               :aria-label "Edit"
               :title "Edit"
               :on-click (fn [e]
                           (.stopPropagation e)
                           (start-edit))}
              [edit-icon]]
             [:button.icon-btn.delete-btn
              {:type "button"
               :aria-label "Delete"
               :title "Delete"
               :on-click (fn [e]
                           (.stopPropagation e)
                           (reset! !confirming? true))}
              [trash-icon]]]]
           (when has-links?
             [:div.task-row-links
              [view/task-links {:links links}]])
           ;; Read-only chips for any linked contacts / notes / tasks, beside the
           ;; URL links. Editing and add/remove live on the detail page; here the
           ;; chips only display and route on click.
           (when has-entity-links?
             [:div.task-row-links
              [sections/entity-extras
               {:entity-uuid    task-uuid
                :editing?       false
                :link-urls      []
                :contacts       contacts
                :linked-notes   linked-notes
                :linked-tasks   linked-tasks
                :on-open-entity (fn [entity] (>e [::notes-data/open-entity entity]))}]])]
          (when open?
            [task-detail {:class (when done? "done")}
             [:div.task-desc description]])])
       (when confirming?
         [delete-confirm
          [:span.prompt "Delete this task?"]
          [:button.delete-no
           {:type "button"
            :on-click (fn [e]
                        (.stopPropagation e)
                        (reset! !confirming? false))}
           "Cancel"]
          [:button.delete-yes
           {:type "button"
            :on-click (fn [e]
                        (.stopPropagation e)
                        (reset! !confirming? false)
                        (>e [::data/delete task-uuid]))}
           "Delete"]])])))
